import React, { useState, useEffect, useRef } from 'react'
import { createFormBag, FormBag, FormData, FormErrors, validateFormBag } from 'react-formage'

import { connect } from 'containers/store'
import { sharedActionCreators } from 'containers/shared'

import { ButtonGroup } from 'components/ui/button'
import { FieldSection } from 'components/ui/fieldset'
import { FooterEditEntity } from 'components/ui/footer'
import { Input } from 'components/ui/input'
import { PrimaryNavigationEditEntity } from 'components/ui/primary-navigation/edit-entity'
import { PopOverMenu, PopOverMenuButton } from 'components/ui/pop-over-menu'
import { SecondaryNavigation } from 'components/ui/secondary-navigation'
import { SelectRef } from 'components/ui/select'
import { StatusGuard } from 'components/ui/status-guard'

import { MediaField } from 'domains/media/components/media-field'
import { releaseControlsBindableActionCreators } from 'domains/release-controls/actions'
import { ReleaseControlUpdate } from 'domains/release-controls/components/release-control-update'

import { PlacedPoint } from 'generated/mos/geo'
import { Media } from 'generated/mos/media'
import { SetReleaseStatusRequest } from 'generated/mos/releasecontrolsmanagement'
import { BuildingLevel, Site } from 'generated/mos/structure'
import { WayfindingMap } from 'generated/mos/org/getty/audio'
import { Amenity, VisitorInfoPage } from 'generated/mos/org/getty/visitorinfo'
import { GetAmenityRequest } from 'generated/mos/org/getty/visitorinfoaccess'
import { UpdateAmenityRequest } from 'generated/mos/org/getty/visitorinfomanagement'

import { pick, areObjectsEqual, areRefValuesEqual } from 'helpers/core'
import { convertEntityToFormValues } from 'helpers/schema'
import { statusSelector, Status } from 'helpers/status'
import { getTranslatableValue } from 'helpers/translatables'

import { LayoutListOrFormPage } from 'layouts/list-or-form-page'

import { actionCreators, GettyActionCreators } from '../../../actions'
import { AmenityDelete } from '../amenity-delete'
import { getAmenitySubnav } from '../subnav'
import { amenitySchema } from '../../../schemas/visitorInfo'
import { GettyStore } from '../../../store'

type FormValues = {
  name: string | undefined;
  headerImageAltText: string | undefined;
  openingHoursText: string | undefined;
  descriptionText: string | undefined;
  extendedDescriptionText: string | undefined;
  locationText: string | undefined;
  buildingLevelRef: BuildingLevel.Ref | undefined;
  longitude: string;
  latitude: string;
  wayfindingMapRef: WayfindingMap.Ref | undefined;
}

type ActionProps =
  Pick<GettyActionCreators, 'gettyGetAmenityRequest' | 'gettyUpdateAmenityRequest'> &
  Pick<typeof releaseControlsBindableActionCreators, "releaseControlsListReleaseControlsRequest" | "releaseControlsSetReleaseStatusRequest"> &
  Pick<typeof sharedActionCreators, 'toastNotification'>

type ConnectedProps = Pick<GettyStore['getty'], 'amenityUpdate'>

type DirectProps = {
  readonly historyPush: (id: string) => void;
  readonly projectId: string;
  readonly refId: string;
}

type Props = ActionProps & ConnectedProps & DirectProps

const AmenityUpdateView = (props: Props) => {
  const {
    amenityUpdate,
    gettyUpdateAmenityRequest,
    gettyGetAmenityRequest,
    historyPush,
    projectId,
    toastNotification,
    refId,
    releaseControlsListReleaseControlsRequest,
    releaseControlsSetReleaseStatusRequest,
  } = props;

  // set up form values
  const [ formBag, setFormBag ] = useState<FormBag<FormValues> | undefined>(undefined)
  const [ imageMedia, setImageMedia ] = useState<Media.Ref | undefined>(undefined)
  const [ iconImageMedia, setIconImageMedia ] = useState<Media.Ref | undefined>(undefined)
  const [ iconSelectedImageMedia, setIconSelectedImageMedia ] = useState<Media.Ref | undefined>(undefined)
  const [ deleteAmenityModalOpen, setDeleteAmenityModalOpen ] = useState(false)

  const backLocation = `/project/${projectId}/visitor-info`

  const data = statusSelector.data(amenityUpdate)
  const amenity = data && data.amenity
  const wayfindingMaps = data && data.wayfindingMaps
  const wayfindingMapsLevels = wayfindingMaps && wayfindingMaps[0].levels

  const hasSubmitted = useRef(false)

  // load initial data
  useEffect(() => {
    if (amenityUpdate.status === Status.Idle || amenity && Amenity.mustRef(amenity.ref).id !== refId) {
      gettyGetAmenityRequest({
        type: GetAmenityRequest.refName,
        ref: {
          typename: Amenity.refName,
          id: refId
        }
      })
      releaseControlsListReleaseControlsRequest([{
        typename: Amenity.refName,
        id: refId
      }])
    }
    hasSubmitted.current = false
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const amenityValues = pick({
      ...Amenity.defaults,
      ...amenity,
    },
    'name',
    'headerImageAltText',
    'openingHoursText',
    'descriptionText',
    'extendedDescriptionText',
    'locationText',
    'location',
    'wayfindingMapRef',
  ) as unknown as Omit<Amenity.Entity, 'type' | 'ref'>

  // setup the initial form values.
  const initialFormValues: FormValues | undefined = amenity ?
    {
      ...convertEntityToFormValues<Amenity.Entity>(
        amenityValues as unknown as Amenity.Entity,
        amenitySchema
      ) as any as FormValues,

      buildingLevelRef: amenityValues.location && BuildingLevel.mustRef(amenityValues.location.levelRef),
      // { type: "Point", coordinates: [p["longitude"], p["latitude"]] };
      longitude: amenityValues.location && amenityValues.location.point && String(amenityValues.location.point.coordinates[0]) || '',
      latitude: amenityValues.location && amenityValues.location.point && String(amenityValues.location.point.coordinates[1]) || '',
    }
    : undefined

  const initialImageValue = amenity && amenitySchema.headerImageRef.read(amenity.headerImageRef) as any as Media.Ref | undefined
  const initialIconImageValue = amenity && amenitySchema.iconImageRef.read(amenity.iconImageRef) as any as Media.Ref | undefined
  const initialIconSelectedImageValue = amenity && amenitySchema.iconSelectedImageRef.read(amenity.iconSelectedImageRef) as any as Media.Ref | undefined

  useEffect(() => {
    // once the entity is ready, initialise the form values, or reinitialise once an update was
    // successful.
    if (amenity && initialFormValues && amenityUpdate.status === Status.Ready) {
      setFormBag(createFormBag(initialFormValues))
      setImageMedia(initialImageValue)
      setIconImageMedia(initialIconImageValue)
      setIconSelectedImageMedia(initialIconSelectedImageValue)
    }

    // if the status is ready and the data had been submitted, trigger success toast.
    if (amenityUpdate.status === Status.Ready && hasSubmitted.current) {
      releaseControlsListReleaseControlsRequest([{
        typename: Amenity.refName,
        id: refId
      }])
      toastNotification({ type: 'success', text: 'Amenity updated' })
      hasSubmitted.current = false
    }

  }, [ amenityUpdate.status ]) // eslint-disable-line react-hooks/exhaustive-deps

  // handle the form ui.
  const modified = initialFormValues && formBag && (
    !areObjectsEqual(initialFormValues, formBag.values) ||
    !areRefValuesEqual(initialImageValue, imageMedia) ||
    !areRefValuesEqual(initialIconImageValue, iconImageMedia) ||
    !areRefValuesEqual(initialIconSelectedImageValue, iconSelectedImageMedia)
  ) || false

  const validateForm = (values: FormValues) => {
    const errors: FormErrors<FormValues> = {};

    if (!values.name || !values.name.trim().length) {
      errors.name = 'Title is required'
    }

    // numeric values.
    if(isNaN(Number(values.longitude))) errors.longitude = 'Longitude must be a number'
    if(isNaN(Number(values.latitude))) errors.latitude = 'Latitude must be a number'

    // check for legit latlong values.
    if(!!values.longitude.length && !isNaN(Number(values.longitude))) {
      if(!isFinite(Number(values.longitude)) || Math.abs(Number(values.longitude)) > 180) {
        errors.longitude = 'Longitude must a number between -180 and 180'
      }
    }
    if(!!values.latitude.length && !isNaN(Number(values.latitude))) {
      if(!isFinite(Number(values.latitude)) || Math.abs(Number(values.latitude)) > 90) {
        errors.latitude = 'Latitude must be a number between -90 and 90'
      }
    }

    // all location values must be truthy, or all values must be falsey.
    const locationValues = [
      BuildingLevel.isRef(values.buildingLevelRef),
      !!values.longitude.length && !isNaN(Number(values.longitude)),
      !!values.latitude.length  && !isNaN(Number(values.latitude)),
    ]
    if(!locationValues.every(v => v === locationValues[0])) {
      errors.buildingLevelRef = 'Building level, longitude and latitude values must all be specified'
    }

    return errors
  }

  const submit = (formBag: FormBag<FormValues>, amenity: Amenity.Entity) => {
    const bag = validateFormBag(formBag, validateForm)
    setFormBag(bag)

    if(!bag.valid) {
      return false
    }

    const {
      name,
      headerImageAltText,
      openingHoursText,
      descriptionText,
      extendedDescriptionText,
      locationText,
      buildingLevelRef,
      latitude,
      longitude,
      wayfindingMapRef,
    } = formBag.values
    hasSubmitted.current = true

    // if all values are available, create a placed point object. all attributes are required in
    // protobuf, but this doesn't seem to be the case with the generated typescript.
    const location: PlacedPoint.Entity | undefined =
    (
      BuildingLevel.isRef(buildingLevelRef) &&
      !isNaN(Number(latitude)) &&
      !isNaN(Number(longitude))
    ) ?
      {
        ...PlacedPoint.defaults,
        levelRef: buildingLevelRef,
        // { type: "Point", coordinates: [p["longitude"], p["latitude"]] };
        point: {
          type: 'Point',
          coordinates: [
            Number(longitude),
            Number(latitude)
          ]
        },
      }
    : undefined

    const updatePayload: UpdateAmenityRequest.Entity = {
      ...UpdateAmenityRequest.defaults,
      ref: Amenity.mustRef(amenity.ref),
      updateMask: {
        type: 'google.protobuf.FieldMask',
        fields: [
          'name',
          'headerImageRef',
          'headerImageAltText',
          'openingHoursText',
          'descriptionText',
          'extendedDescriptionText',
          'locationText',
          'location',
          'wayfindingMapRef',
          'iconImageRef',
          'iconSelectedImageRef',
        ]
      },
      name: amenitySchema.name.write(name, amenity.name) || name,
      headerImageAltText: amenitySchema.headerImageAltText.write(headerImageAltText, amenity.headerImageAltText) || headerImageAltText,
      openingHoursText: amenitySchema.openingHoursText.write(openingHoursText, amenity.openingHoursText) || openingHoursText,
      descriptionText: amenitySchema.descriptionText.write(descriptionText, amenity.descriptionText) || descriptionText,
      extendedDescriptionText: amenitySchema.descriptionText.write(extendedDescriptionText, amenity.extendedDescriptionText) || extendedDescriptionText,
      locationText: amenitySchema.locationText.write(locationText, amenity.locationText) || locationText,
      headerImageRef: amenitySchema.headerImageRef.write(imageMedia) || imageMedia,
      location,
      wayfindingMapRef,
      iconImageRef: amenitySchema.iconImageRef.write(iconImageMedia) || iconImageMedia,
      iconSelectedImageRef: amenitySchema.iconSelectedImageRef.write(iconSelectedImageMedia) || iconSelectedImageMedia,
    }
    gettyUpdateAmenityRequest(updatePayload)

    return false
  }

  const releaseControlUpdate = (bundles: ReadonlyArray<SetReleaseStatusRequest.BundleStatus.Entity>) => {
    amenity && releaseControlsSetReleaseStatusRequest({
      ...SetReleaseStatusRequest.defaults,
      ref: amenity.ref,
      bundles,
    })
  }

  return (
    <>
      {deleteAmenityModalOpen && amenity && (
        <AmenityDelete onCancel={() => setDeleteAmenityModalOpen(false)} amenity={amenity} onComplete={() => historyPush(backLocation)} />
      )}
      <LayoutListOrFormPage
        nav={() => (
          <PrimaryNavigationEditEntity
            title="Edit page"
            onConfirm={modified ? () => historyPush(backLocation) : undefined}
            navBackRoute={backLocation}
          />
        )}
        title={amenity && amenity.name && amenitySchema.name ? amenitySchema.name.read(amenity.name) as string : '' }
        toolbar={() => (
          <ButtonGroup>
            {amenity &&
              <ReleaseControlUpdate
                hasBorder
                entityRef={amenity.ref}
                updateHandler={releaseControlUpdate}
              />
            }
            <PopOverMenu hasBorder={true}>
              <PopOverMenuButton onClick={() => setDeleteAmenityModalOpen(true)}>
                Delete
              </PopOverMenuButton>
            </PopOverMenu>
          </ButtonGroup>
        )}
        subnav={() => (
          <SecondaryNavigation
            navItems= {getAmenitySubnav(projectId, refId)}
            modified={modified}
            historyPush={historyPush}
          />
        )}
        content={() => (
          <StatusGuard status={amenityUpdate} mode="form">
            {formBag && amenity && (
              <form noValidate onSubmit={(event) => { event.preventDefault() }}>
                <FormData
                  bag={formBag}
                  onUpdate={(event) => {
                    setFormBag(event.bag)
                  }}
                  validate={() => ({})}
                >
                  <Input
                    label="Title"
                    field="name"
                    error={formBag.touched.name ? formBag.errors.name : undefined}
                  />
                  <FieldSection />
                  <MediaField
                    kind="MEDIA_KIND_RASTER_IMAGE"
                    mediaRef={imageMedia}
                    onUpdate={(entity) => { setImageMedia(entity ? entity.ref : undefined) }}
                    label="Image"
                  />
                  <Input
                    label="Alternate Text"
                    description="Description of what the image contains if it cannot be shown."
                    field="headerImageAltText"
                    isAssociated={true}
                  />
                  <Input
                    component="textarea"
                    label="Description"
                    field="descriptionText"
                  />
                  <Input
                    component="textarea"
                    label="Extended Description"
                    field="extendedDescriptionText"
                  />
                  <Input
                    component="textarea"
                    label="Opening hours"
                    field="openingHoursText"
                  />
                  <Input
                    label="Location text"
                    field="locationText"
                  />
                  <MediaField
                    kind="MEDIA_KIND_RASTER_IMAGE"
                    mediaRef={iconImageMedia}
                    onUpdate={(entity) => { setIconImageMedia(entity ? entity.ref : undefined) }}
                    label="Icon Image"
                  />
                  <MediaField
                    kind="MEDIA_KIND_RASTER_IMAGE"
                    mediaRef={iconSelectedImageMedia}
                    onUpdate={(entity) => { setIconSelectedImageMedia(entity ? entity.ref : undefined) }}
                    label="Icon Selected Image"
                  />
                  <FieldSection />
                  {wayfindingMaps && wayfindingMaps.length && (
                    <SelectRef<FormValues, "wayfindingMapRef">
                      label="Wayfinding map"
                      field="wayfindingMapRef"
                      isClearable
                      options={wayfindingMaps.map((item) => ({
                        label: getTranslatableValue(item.altText) as string,
                        value: WayfindingMap.mustRef(item.ref),
                      }))}
                      error={formBag.touched.wayfindingMapRef ? formBag.errors.wayfindingMapRef : undefined}
                    />
                  )}
                  {wayfindingMapsLevels && wayfindingMapsLevels.length && (
                    <>
                      <SelectRef<FormValues, "buildingLevelRef">
                        label="Building level"
                        field="buildingLevelRef"
                        isClearable
                        options={wayfindingMapsLevels.map((item) => ({
                          label: item.abbreviation,
                          value: BuildingLevel.mustRef(item.levelRefs[0]),
                        }))}
                        error={formBag.touched.buildingLevelRef ? formBag.errors.buildingLevelRef : undefined}
                      />
                      <Input
                        label="Longitude"
                        field="longitude"
                        error={formBag.touched.longitude ? formBag.errors.longitude : undefined}
                        isAssociated={true}
                      />
                      <Input
                        label="Latitude"
                        field="latitude"
                        error={formBag.touched.latitude ? formBag.errors.latitude : undefined}
                        isAssociated={true}
                      />
                    </>
                  )}
                </FormData>
              </form>
            )}
          </StatusGuard>
        )}
        sidebar={() => null}
        footer={() => formBag && amenity && (
          <FooterEditEntity
            entity={amenityUpdate}
            modified={modified}
            onCancel={() => historyPush(backLocation)}
            onSave={() => {
              submit(formBag, amenity);
              return false;
            }}
          />
        )}
      />
    </>
  )
}

export const AmenityUpdate = connect<ConnectedProps, ActionProps, DirectProps, GettyStore, GettyActionCreators>(
  store => ({
    ...pick(store.getty, 'amenityUpdate'),
  }),
  {
    ...pick(actionCreators, 'gettyGetAmenityRequest', 'gettyUpdateAmenityRequest'),
    ...pick(releaseControlsBindableActionCreators, 'releaseControlsListReleaseControlsRequest', 'releaseControlsSetReleaseStatusRequest'),
    ...pick(sharedActionCreators, 'toastNotification'),
  },
)(AmenityUpdateView)
